import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { getAllCustomers } from "../../services/customerService";
import { FaRupeeSign, FaPlus, FaTruck } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeLetter, setActiveLetter] = useState(""); // State for letter touch indicator
  const navigate = useNavigate();
  const location = useLocation();
  const { group_id, group_name } = location.state || {}; // Get group_id from navigation state

  // Fetch all customers on component mount
  useEffect(() => {
    fetchCustomers();
  }, [group_id]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await getAllCustomers(1, 300); // Adjust the pagination limit as needed
      let customerList = response.data;

      // Filter by group_id if provided
      if (group_id) {
        customerList = customerList.filter(
          (customer) => customer.group_id === group_id
        );
      }

      setCustomers(customerList);
      setFilteredCustomers(customerList);
    } catch (error) {
      console.error("Error fetching customers:", error.message);
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  // Filter customers based on search query
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    if (query.trim() === "") {
      // Reset filtered list when query is empty
      setFilteredCustomers(customers);
    } else {
      const filtered = customers.filter((customer) =>
        customer.name.toLowerCase().includes(query) ||
        customer.mobile.toLowerCase().includes(query) ||
        customer.address.toLowerCase().includes(query)
      );
      setFilteredCustomers(filtered);
    }
  };

  // Scroll to a specific alphabet
  const scrollToAlphabet = (letter) => {
    const element = document.querySelector(`#customer-${letter}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveLetter(letter); // Set active letter
      setTimeout(() => setActiveLetter(""), 1000); // Clear active letter after 1 second
    }
  };

  const handleCustomerClick = (customer) => {
    navigate(`/customer-profile/${customer.id}`, { state: { customer } });
  };

  const handleDeliveryClick = (customer) => {
    navigate(`/delivery-options/${customer.id}`, { state: { customer } });
  };

  return (
    <div className="max-w-4xl mx-auto pt-1 pb-8 relative">
      {/* Header */}
      <h1 className="text-2xl font-semibold text-center mb-4">
        {group_name ? `Customer List for Group ${group_name}` : "Customer List"}
      </h1>

      {/* Search Input */}
      <input
        type="text"
        value={searchQuery}
        onChange={handleSearch}
        placeholder="Search by name"
        className="w-full p-3 rounded-md border border-gray-300 mb-4 focus:ring focus:ring-blue-300"
      />

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="flex -mt-6">
          {/* Customer List */}
          <div className="flex-1">
            {filteredCustomers.length === 0 ? (
              <p className="text-center text-gray-500">No customers found.</p>
            ) : (
              [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].map((letter) => {
                const groupedCustomers = filteredCustomers.filter((customer) =>
                  customer.name.toUpperCase().startsWith(letter)
                );

                return (
                  groupedCustomers.length > 0 && (
                    <div key={letter}>
                      <h2
                        id={`customer-${letter}`}
                        className="font-semibold text-lg text-gray-700 mt-2"
                      >
                        {letter}
                      </h2>
                      <hr className="mb-2" />
                      {groupedCustomers.map((customer) => (
                        <div
                          key={customer.id}
                          className="bg-white p-4 rounded-md shadow-md mb-3 flex items-center justify-between"
                        >
                          {/* Customer Details */}
                          <div
                            onClick={() => handleCustomerClick(customer)}
                            className="cursor-pointer"
                          >
                            <p className="text-lg font-medium">
                              {customer.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              Mobile: {customer.mobile}
                            </p>
                            <p className="text-sm text-gray-500">
                              Address: {customer.address}
                            </p>
                            {/* Small Hint */}
                            <p className="text-xs text-gray-500 mt-1">
                              ✏️ Update customer & payment
                            </p>
                          </div>

                          {/* Advance/Due Amount + Delivery Button */}
                          <div
                            className="text-right cursor-pointer"
                            onClick={() => handleDeliveryClick(customer)}
                          >
                            <p className="text-green-600 font-bold text-sm">
                              <FaRupeeSign className="inline mr-1" />
                              Credit: {customer.advance_amount || 0}
                            </p>
                            <p className="text-red-600 font-bold text-sm">
                              <FaRupeeSign className="inline mr-1" />
                              Due: {customer.due_amount || 0}
                            </p>
                            <button
                              onClick={() => handleDeliveryClick(customer)}
                              className="-mt-2 bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
                            >
                              <FaTruck className="inline mr-2" />
                              Delivery
                            </button>
                            {/* Small Hint */}
                            <p className="text-xs text-gray-500 mt-1">
                              📦 Quick delivery & invoice
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                );
              })
            )}
          </div>

          {/* Fixed Alphabetical Scroll */}
          <div className="fixed top-20 right-2 bg-white p-0 rounded-md shadow-md flex flex-col items-center z-1">
            {[..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"].map((letter) => (
              <div
                key={letter}
                onClick={() => scrollToAlphabet(letter)}
                className={`cursor-pointer text-center text-blue-500 font-bold py-1 px-2 rounded-full hover:bg-blue-100 ${
                  activeLetter === letter ? "bg-blue-500 text-white" : ""
                }`}
              >
                {letter}
              </div>
            ))}
          </div>

          {/* Active Letter Indicator */}
          {activeLetter && (
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white text-2xl font-bold rounded-full p-3 shadow-lg">
              {activeLetter}
            </div>
          )}
        </div>
      )}

      {/* Floating Add Button */}
      <button
        onClick={() => navigate("/create-customer")}
        className="bg-green-500 text-white rounded-full w-14 h-14 flex items-center justify-center fixed bottom-10 right-4 shadow-lg hover:bg-green-600"
      >
        <FaPlus className="text-2xl" />
      </button>
    </div>
  );
};

export default CustomerList;
